import { Controller } from "@hotwired/stimulus";

// Counts a number up to its final value when it scrolls into view. The final
// value is the element's own server-rendered text (e.g. "24h"), parsed into a
// leading integer plus a suffix — so without JS, under reduced motion, or for a
// non-numeric value, the element just shows that text and nothing animates.
// Plain requestAnimationFrame with an ease-out curve; no animation library.
export default class extends Controller {
  static values = { duration: { type: Number, default: 1.2 } };

  connect() {
    // Skip Turbo's cached preview so the count only runs on the real page.
    if (document.documentElement.hasAttribute("data-turbo-preview")) {return;}

    const match = this.element.textContent.trim().match(/^(\d[\d\s,.]*)(\D*)$/);
    if (!match) {return;} // not a number (e.g. "PL + EN", "RODO") — leave as-is.

    this.target = Number(match[1].replace(/[\s,.]/g, ""));
    this.suffix = match[2];
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {return;}

    this.element.textContent = `0${this.suffix}`;
    // Start once the element's top crosses 90% of the viewport height (the old
    // ScrollTrigger `start: "top 90%"`), and only once.
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {return;}

      this.observer.disconnect();
      this.observer = null;
      this.start();
    }, { rootMargin: "0px 0px -10% 0px" });
    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
    // If torn down mid-count (Turbo navigation), snap to the final value.
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.finish();
    }
  }

  start() {
    const startedAt = performance.now();
    const durationMs = this.durationValue * 1000;
    const step = (now) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      const eased = 1 - (1 - progress) * (1 - progress); // ease-out (quadratic)
      this.element.textContent = `${Math.round(this.target * eased)}${this.suffix}`;
      if (progress < 1) {
        this.frame = requestAnimationFrame(step);
      } else {
        this.frame = null;
        this.finish();
      }
    };
    this.frame = requestAnimationFrame(step);
  }

  finish() {
    this.element.textContent = `${this.target}${this.suffix}`;
  }
}
